/**
 * SEO Component
 *
 * Handles meta tags for search engines and social sharing, plus
 * Organization + WebSite JSON-LD structured data for richer Google
 * results (Knowledge Panel, brand sitelinks).
 *
 * Used with Gatsby's Head API in page/template exports. Defaults come
 * from siteMetadata in gatsby-config.js. Any value passed as a prop
 * overrides the default, so individual Prismic documents can fully
 * control their SEO.
 *
 * Usage in a page:
 *   export const Head = ({ data, location }) => (
 *     <Seo
 *       title={data.prismicX.data.meta_title}
 *       description={data.prismicX.data.meta_description}
 *       image={data.prismicX.data.meta_image?.url}
 *       pathname={location.pathname}
 *     />
 *   );
 */

import React from "react";
import { useStaticQuery, graphql } from "gatsby";

const Seo = ({
  title,
  description,
  image,
  pathname,
  noindex = false,
  children,
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          titleTemplate
          description
          siteUrl
          siteName
          locale
          defaultImage
          defaultImageWidth
          defaultImageHeight
          social {
            instagram
            linkedin
          }
        }
      }
    }
  `);

  const {
    title: defaultTitle,
    titleTemplate,
    description: defaultDescription,
    siteUrl,
    siteName,
    locale,
    defaultImage,
    defaultImageWidth,
    defaultImageHeight,
    social,
  } = site.siteMetadata;

  const formattedTitle = title
    ? titleTemplate?.replace(`%s`, title) || `${title} | ${siteName}`
    : defaultTitle;

  // Decide which image to use and whether we know its true dimensions.
  // We only emit og:image:width/height when we know them for sure
  // (i.e. for the bundled default). Prismic-uploaded images skip the
  // hints so we never lie to crawlers about size/aspect.
  const usingDefaultImage = !image;
  const resolvedImage = image || defaultImage;
  const absoluteImage = resolvedImage
    ? resolvedImage.startsWith(`http`)
      ? resolvedImage
      : `${siteUrl}${resolvedImage}`
    : null;

  const seo = {
    title: formattedTitle,
    description: description || defaultDescription,
    image: absoluteImage,
    url: pathname ? `${siteUrl}${pathname}` : siteUrl,
  };

  // Organization schema — appears on every page and powers Google's
  // brand Knowledge Panel, logo display, and social profile links.
  const socialUrls = Object.values(social || {}).filter(Boolean);
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/favicon.png`,
    description: defaultDescription,
    ...(socialUrls.length > 0 ? { sameAs: socialUrls } : {}),
  };

  // WebSite schema — describes the site as a whole. Useful for the
  // canonical site name in Google results.
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: siteUrl,
    inLanguage: locale.replace(`_`, `-`),
  };

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Canonical */}
      <link rel="canonical" href={seo.url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.url} />
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.image && (
        <meta property="og:image:alt" content={`${siteName} logo`} />
      )}
      {seo.image && usingDefaultImage && (
        <>
          <meta property="og:image:width" content={String(defaultImageWidth)} />
          <meta
            property="og:image:height"
            content={String(defaultImageHeight)}
          />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.image && <meta name="twitter:image" content={seo.image} />}

      {/* Structured data — Organization + WebSite */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* Favicons, apple-touch-icons, and the web manifest are injected
          globally by gatsby-plugin-manifest (see gatsby-config.js). */}

      {children}
    </>
  );
};

export default Seo;
